/********************************/
/*          About vuex          */
/********************************/
// The MIT License (MIT)
//
// Copyright (c) 2015-present Evan You
//
// Permission is hereby granted, free of charge, to any person obtaining a copy
// of this software and associated documentation files (the "Software"), to deal
// in the Software without restriction, including without limitation the rights
// to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
// copies of the Software, and to permit persons to whom the Software is
// furnished to do so, subject to the following conditions:
//
// The above copyright notice and this permission notice shall be included in
// all copies or substantial portions of the Software.
//
// THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
// IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
// FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
// AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
// LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
// OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
// THE SOFTWARE.

import { mapState, mapActions } from 'vuex';
import individualDiagnose from '../../../../share/util/individualDiagnose';
import individualWorkSupportUtil from '../../../../share/util/individualWorkSupportUtil';
import config from '../../../../share/config';
import session from '../../../../share/session';

/** 各フェーズ定義 */
const PHASE = {
  START: 'start',                                // 待機画面
  EXECUTION: 'execution',                        // 実行画面
  EXECUTION_MONITORING: 'execution_monitoring',  // 実行監視画面
  SUCCESS: 'success',                            // 停止画面
  ERROR: 'error',                                // エラー画面
  FAILED: 'failed'                               // 共通エラー画面
};

/** Backendから返却されたレスポンス値 */
const ROUTINERESULT = {
  IN_COMPLETE: 'inComplete',
  TIRE1_COMPLETE: 'tire1Complete',
  TIRE2_COMPLETE: 'tire2Complete',
  TIRE3_COMPLETE: 'tire3Complete',
  ALL_COMPLETE: 'allComplete',
  NOT_ID_REGISTER: 'notIdRegisterError',
  COMMON_ERROR: 'commonError'
};

export default {
  name: 'requirement093',
  data() {
    return {
      phase: PHASE.START,                        // 画面フェーズ（初期値は待機画面）
      systemId: '',                              // システムID
      isLoading: false,                          // スピナー表示
      displayInfo: '',                           // 画面表示用文言
      tireList: [],                              // タイヤ表示用リスト
      isDisabled: false,                         // 実行監視画面の処理取得を行わない為のフラグ
      isControlDisabled: false,                  // 実行監視画面のキャンセルボタン押下制御用フラグ
      modal_requirement_093: false
    };
  },
  props: {
    i18nWsKey: { type: String, default: '', required: true },
    workSupportId: { type: Number, default: '', required: true },
  },

  computed: {
    ...mapState({
      netAppId: state => state.netAppConnector.pairingAppId
    }),

    /** ダイアログのヘッダー色 */
    headerColor() {
      switch (this.phase) {
        case PHASE.ERROR:
        case PHASE.FAILED:
          return 'danger';
        case PHASE.SUCCESS:
          return 'success';
        case PHASE.START:
        case PHASE.EXECUTION:
        case PHASE.EXECUTION_MONITORING:
        default:
          return 'secondary';
      }
    },

    /** 実行監視画面テーブル */
    tireData() {
      return [
        { name: 'tire1', label: this.$t(`${this.i18nWsKey}.label_tire1`), value: '', isComplete: false },
        { name: 'tire2', label: this.$t(`${this.i18nWsKey}.label_tire2`), value: '', isComplete: false },
        { name: 'tire3', label: this.$t(`${this.i18nWsKey}.label_tire3`), value: '', isComplete: false },
        { name: 'tire4', label: this.$t(`${this.i18nWsKey}.label_tire4`), value: '', isComplete: false },
      ];
    }
  },

  methods: {
    ...mapActions('auth', ['logout', 'resetSession']),

    /** 初期化処理 */
    init() {
      this.modal_requirement_093 = true;
      // EUCキーの取得
      this.systemId = individualDiagnose.getCurrentSystemId(this);
      this.displayInfo = this.$t(`${this.i18nWsKey}.label_id_registrants_mode`);
    },

    /** 待機画面OKボタン処理 */
    async onClickOkRegistermode() {
      try {
        // スピナー開始
        this.isLoading = true;
        const token = session.getToken();

        // 開始処理
        const response = await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'standby'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 実行画面に遷移
              this.displayInfo = this.$t(`${this.i18nWsKey}.label_register_transmitter`);
              this.phase = PHASE.EXECUTION;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行画面OKボタン処理 */
    async onClickOkRegister() {
      try {
        // スピナー開始
        this.isLoading = true;
        const token = session.getToken();

        // ルーチン処理
        const response = await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'start'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // response結果で画面遷移する
        if (response) {
          // Backendからのレスポンスデータを確認する処理
          await this.confirmRoutineStatus(response);
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 実行画面キャンセルボタン処理 */
    async onClickCancelRegister() {
      try {
        // スピナー開始
        this.isLoading = true;
        const token = session.getToken();

        // DefaultSession遷移
        const response = await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'notInitStandby'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // 実行画面に遷移
              this.displayInfo = this.$t(`${this.i18nWsKey}.label_register_transmitter`);
              this.phase = PHASE.EXECUTION;
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * タイヤ表表示処理
     * @param {String} routineStatus ルーチンステータス
     */
    async tireDataDisplay(routineStatus) {
      const tableData = this.tireData;
      if (routineStatus === ROUTINERESULT.IN_COMPLETE) {
        tableData.forEach(element => {
          if (!element.isComplete) {
            element.value = this.$t(`${this.i18nWsKey}.label_unfinished`);
          }
        });
      } else if (routineStatus === ROUTINERESULT.TIRE1_COMPLETE) {
        tableData.forEach((element, i) => {
          if (i === 0) {
            element.value = this.$t(`${this.i18nWsKey}.label_finished`);
            element.isComplete = true;
          } else {
            if (!element.isComplete) {
              element.value = this.$t(`${this.i18nWsKey}.label_unfinished`);
            }
          }
        });
      } else if (routineStatus === ROUTINERESULT.TIRE2_COMPLETE) {
        tableData.forEach((element, i) => {
          if (i === 0 || i === 1) {
            element.value = this.$t(`${this.i18nWsKey}.label_finished`);
            element.isComplete = true;
          } else {
            if (!element.isComplete) {
              element.value = this.$t(`${this.i18nWsKey}.label_unfinished`);
            }
          }
        });
      } else if (routineStatus === ROUTINERESULT.TIRE3_COMPLETE) {
        tableData.forEach((element, i) => {
          if (i === 0 || i === 1 || i === 2) {
            element.value = this.$t(`${this.i18nWsKey}.label_finished`);
            element.isComplete = true;
          } else {
            if (!element.isComplete) {
              element.value = this.$t(`${this.i18nWsKey}.label_unfinished`);
            }
          }
        });
      }
      // シャローコピーなので tableData の中にオブジェクトが含まれている場合、そのオブジェクト自体はコピーされません。
      this.tireList = [...tableData];
    },

    /** 実行監視画面表示 */
    async updateExecutionMonitoring() {
      try {
        // スピナー開始
        this.isLoading = true;
        const token = session.getToken();

        // ルーチン処理
        const response = await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'execute'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // キャンセルボタン押下されている場合、executeのレスポンスを無視する
        if (!this.isDisabled) {
          // Backendからのレスポンスデータを確認する処理
          await this.confirmRoutineStatus(response);
        }
      } finally {
        this.isLoading = false;
      }
    },

    /**
     * 実行/実行監視画面のBackendレスポンス結果確認処理
     * @param {Object} response レスポンス
     */
    async confirmRoutineStatus(response) {
      if (response) {
        let routineStatus = null;
        switch (response.data.status) {
          case config.STATUS_WSP_COMPLETED:
            routineStatus = response.data.user_data.routine_status;
            // ルーチンステータス
            if (routineStatus === ROUTINERESULT.ALL_COMPLETE) {
              // 停止画面に遷移
              this.displayInfo = this.$t(`${this.i18nWsKey}.label_registration_completed`);
              this.phase = PHASE.SUCCESS;
              break;
            } else if (routineStatus === ROUTINERESULT.NOT_ID_REGISTER) {
              // エラー画面遷移
              this.displayInfo = this.$t(`${this.i18nWsKey}.label_not_id_registration`);
              this.phase = PHASE.ERROR;
              break;
            } else if (routineStatus === ROUTINERESULT.COMMON_ERROR) {
              // 共通エラー画面遷移
              this.showUnexpectedError();
              break;
            }
            if (routineStatus === ROUTINERESULT.IN_COMPLETE || routineStatus === ROUTINERESULT.TIRE1_COMPLETE ||
              routineStatus === ROUTINERESULT.TIRE2_COMPLETE || routineStatus === ROUTINERESULT.TIRE3_COMPLETE) {
              // タイヤ表更新処理
              await this.tireDataDisplay(routineStatus);
              // 実行監視状態に遷移
              this.phase = PHASE.EXECUTION_MONITORING;
              await this.updateExecutionMonitoring();
            }
            break;
          case config.STATUS_WSP_FAILED:
          default:
            this.showUnexpectedError();
            break;
        }
      }
    },

    /** 実行監視画面キャンセルボタン処理 */
    async onClickCancelExecutionMonitoring() {
      try {
        // スピナー開始
        this.isLoading = true;
        const token = session.getToken();

        // キャンセルボタン押下不可フラグ
        this.isControlDisabled = true;

        // 機能終了処理
        await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'end'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        // スピナーが途中で止まり画面表示が残ってしまう為、キャンセルボタン押下不可フラグと分けて設定する
        this.isDisabled = true;

        // レスポンスの結果に関わらず、機能終了
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** 停止画面OKボタン/エラー画面OKボタン処理 */
    async onClickOkExit() {
      try {
        // スピナー開始
        this.isLoading = true;
        const token = session.getToken();

        // 終了処理
        const response = await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバックを実装する。
          // ここでは利用していないが、引数の errorType にはあらかじめ用意された定数文字列が返ってくる。
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバックを定義する（validateErrorResult の中は同様のため、省略する）
          (result) => { this.validateErrorResult(result); },
        );

        if (response) {
          switch (response.data.status) {
            case config.STATUS_WSP_COMPLETED:
              // Wizardを閉じる
              this.closeWizard();
              break;
            case config.STATUS_WSP_FAILED:
            default:
              this.showUnexpectedError();
              break;
          }
        }
      } finally {
        this.isLoading = false;
      }
    },

    /** 終了処理 */
    async exitProcess() {
      try {
        // スピナー開始
        this.isLoading = true;
        const token = session.getToken();
        // 終了処理
        await individualWorkSupportUtil.pollingWspApi(
          this, this.netAppId, this.systemId, this.workSupportId,
          {
            id_token: token.id_token,
            type: 'exit'
          },
          // エラー発生のコールバック
          (errorType) => { errorType; this.showUnexpectedError(); },
          // POST API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
          // GET API 結果が異常の場合（handler.validate でエラーが発生した場合）のコールバック
          (result) => { this.validateErrorResult(result); },
        );
        // 終了処理の結果に関係なくダイアログを閉じる
        this.closeWizard();
      } finally {
        this.isLoading = false;
      }
    },

    /** Wizardを閉じる */
    async closeWizard() {
      this.$emit('close-wizard');
      this.modal_requirement_093 = false;
    },

    /**
     * API 通信で共通バリデーションでエラーが発生した際のコールバック
     * @param {object} result バリデーション結果
     */
    validateErrorResult(result) {
      individualDiagnose.apiErrorDefaultAction(
        this, result,
        // ログアウト処理のコールバック
        (self) => { self; this.logout(this); },
        // セッションリセット処理のコールバック
        (self) => { self; this.resetSession(this); },
        // その他エラー発生のコールバック
        (result) => { result; this.showUnexpectedError(); },
      );
    },

    /** その他エラー発生時の表示 */
    showUnexpectedError() {
      // 異常終了：共通エラー遷移
      this.phase = PHASE.FAILED;
    },
  },

  async mounted() {
    this.init();
  },
};